import { supabase } from './supabase';
import { Bookmark, CreateBookmarkData, UpdateBookmarkData } from '../types';

export class BookmarkService {
  private static instance: BookmarkService;
  private bookmarkCache: Map<string, boolean> = new Map();
  private cacheTimeout: Map<string, number> = new Map();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  static getInstance(): BookmarkService {
    if (!BookmarkService.instance) {
      BookmarkService.instance = new BookmarkService();
    }
    return BookmarkService.instance;
  }

  private getCacheKey(userId: string, docketNumber: string): string {
    return `${userId}:${docketNumber}`;
  }

  private isCacheValid(cacheKey: string): boolean {
    const timestamp = this.cacheTimeout.get(cacheKey);
    return timestamp ? Date.now() < timestamp : false;
  }

  private setCache(cacheKey: string, isBookmarked: boolean): void {
    this.bookmarkCache.set(cacheKey, isBookmarked);
    this.cacheTimeout.set(cacheKey, Date.now() + this.CACHE_DURATION);
  }

  private clearCache(): void {
    this.bookmarkCache.clear();
    this.cacheTimeout.clear();
  }

  // Public method to clear cache (useful for logout or refresh)
  clearAllCache(): void {
    this.clearCache();
  }

  async getBookmarks(): Promise<Bookmark[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      console.log('Fetching bookmarks for user:', user.id);

      const { data, error } = await supabase
        .from('bookmarks')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching bookmarks:', error);
        throw error;
      }
      
      console.log('Successfully fetched bookmarks:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
      throw error;
    }
  }

  /**
   * Get all bookmarked docket numbers for the current user in a single query.
   * Returns a Set for efficient O(1) lookups.
   */
  async getAllBookmarksForUser(): Promise<Set<string>> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // Return empty set for unauthenticated users
        return new Set<string>();
      }

      const { data, error } = await supabase
        .from('bookmarks')
        .select('docket_number')
        .eq('user_id', user.id);

      if (error) {
        console.error('Error fetching all bookmarks:', error);
        // Return empty set on error instead of throwing
        return new Set<string>();
      }

      // Create a Set of bookmarked docket numbers for O(1) lookups
      const bookmarkedDockets = new Set<string>();
      (data || []).forEach(bookmark => {
        if (bookmark.docket_number) {
          bookmarkedDockets.add(bookmark.docket_number);
        }
      });

      // Update cache with all fetched bookmarks
      data?.forEach(bookmark => {
        if (bookmark.docket_number) {
          const cacheKey = this.getCacheKey(user.id, bookmark.docket_number);
          this.setCache(cacheKey, true);
        }
      });

      return bookmarkedDockets;
    } catch (error) {
      console.error('Error fetching all bookmarks:', error);
      return new Set<string>();
    }
  }

  async createBookmark(bookmarkData: CreateBookmarkData): Promise<Bookmark> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      console.log('Creating bookmark for user:', user.id, 'docket:', bookmarkData.docket_number);

      const { data, error } = await supabase
        .from('bookmarks')
        .insert({
          user_id: user.id,
          docket_number: bookmarkData.docket_number,
          title: bookmarkData.title,
          notes: bookmarkData.notes
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating bookmark:', error);
        throw error;
      }
      
      console.log('Successfully created bookmark:', data);
      // Clear cache for this docket
      const cacheKey = this.getCacheKey(user.id, bookmarkData.docket_number);
      this.bookmarkCache.delete(cacheKey);
      this.cacheTimeout.delete(cacheKey);
      return data;
    } catch (error) {
      console.error('Error creating bookmark:', error);
      throw error;
    }
  }

  async updateBookmark(id: string, updateData: UpdateBookmarkData): Promise<Bookmark> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('bookmarks')
        .update(updateData)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      // Clear cache for this docket
      const cacheKey = this.getCacheKey(user.id, updateData.docket_number || '');
      this.bookmarkCache.delete(cacheKey);
      this.cacheTimeout.delete(cacheKey);
      return data;
    } catch (error) {
      console.error('Error updating bookmark:', error);
      throw error;
    }
  }

  async deleteBookmark(id: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabase
        .from('bookmarks')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
      // Clear cache for this docket (we need to get the docket number first)
      const { data: bookmark } = await supabase
        .from('bookmarks')
        .select('docket_number')
        .eq('id', id)
        .single();
      
      if (bookmark) {
        const cacheKey = this.getCacheKey(user.id, bookmark.docket_number);
        this.bookmarkCache.delete(cacheKey);
        this.cacheTimeout.delete(cacheKey);
      }
    } catch (error) {
      console.error('Error deleting bookmark:', error);
      throw error;
    }
  }

  async isBookmarked(docketNumber: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No authenticated user, returning false for bookmark check');
        return false;
      }

      const cacheKey = this.getCacheKey(user.id, docketNumber);
      
      // Check cache first
      if (this.bookmarkCache.has(cacheKey) && this.isCacheValid(cacheKey)) {
        const cachedResult = this.bookmarkCache.get(cacheKey);
        return cachedResult!;
      }

      // Perform the actual bookmark check
      const { data, error } = await supabase
        .from('bookmarks')
        .select('id')
        .eq('user_id', user.id)
        .eq('docket_number', docketNumber)
        .maybeSingle(); // Use maybeSingle instead of single to avoid errors when no record found

      if (error) {
        console.error('Error checking bookmark status:', error);
        // Don't throw error, just return false
        return false;
      }

      const isBookmarked = !!data;
      
      // Cache the result
      this.setCache(cacheKey, isBookmarked);
      
      return isBookmarked;
    } catch (error) {
      console.error('Error checking bookmark status:', error);
      return false;
    }
  }

  async toggleBookmark(docketNumber: string, title?: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const isBookmarked = await this.isBookmarked(docketNumber);
      
      if (isBookmarked) {
        // Remove bookmark
        const { data: existingBookmark } = await supabase
          .from('bookmarks')
          .select('id')
          .eq('user_id', user.id)
          .eq('docket_number', docketNumber)
          .maybeSingle();

        if (existingBookmark) {
          await this.deleteBookmark(existingBookmark.id);
        }
        // Clear cache for this docket
        const cacheKey = this.getCacheKey(user.id, docketNumber);
        this.bookmarkCache.delete(cacheKey);
        this.cacheTimeout.delete(cacheKey);
        return false;
      } else {
        // Add bookmark
        await this.createBookmark({
          docket_number: docketNumber,
          title: title || `Docket ${docketNumber}`
        });
        // Cache is already cleared in createBookmark
        return true;
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      throw error;
    }
  }
}

export const bookmarkService = BookmarkService.getInstance();
